import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';
import AlbumPhotoModal from './AlbumPhotoModal';
import { ReactComponent as DeleteIcon } from '../../../images/icons/ProductSelectionPage/deleteicon.svg';
import httpClient from '../../../httpClient';
import { apiErrorMessage, getDisplayNumber, getImageId } from '../../../lib/utils';
import { useConfirmationModal } from '../../../hooks';

// initialize images
const initializeItems = (imageArray) => {
  if (!Array.isArray(imageArray)) return [];

  // Sort by filename numerically
  const sorted = [...imageArray].sort((a, b) => {
    const fileA = a.attributes?.photoFileName || '';
    const fileB = b.attributes?.photoFileName || '';
    return fileA.localeCompare(fileB, undefined, { numeric: true, sensitivity: 'base' });
  });

  return sorted.map((image, i) => ({
    ...image,
    id: getImageId(image, i),
    originalIndex: i,
    displayNumber: i + 1,
  }));
};

const ImageCard = ({ index, image, placeholder, onRequestDelete, onImageClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const displayNumber = getDisplayNumber(index);

  const imageUrl = image?.attributes?.smallestPhoto || image?.attributes?.presignedPhotoUrl || image?.attributes?.photoUrl || '';
  const fileName = image?.attributes?.photoFileName || placeholder?.fileName || '';

  const isUploading = !!placeholder;
  const isFetching = !!image && !!imageUrl && !imageLoaded;

  const showLoader = isUploading || isFetching;
  const loaderText = isUploading ? 'Uploading Image...' : 'Loading Image...';

  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onRequestDelete?.(index);
  }, [onRequestDelete, index]);

  const handleCardClick = useCallback(() => {
    if (imageUrl) onImageClick?.(imageUrl, fileName);
  }, [imageUrl, fileName, onImageClick]);

  return (
    <div className={`c-card__one ${imageLoaded ? styles.albumCardWithOverlay : ''}`} onClick={handleCardClick}>
      <div className="div-block-349">{displayNumber}</div>

      {showLoader && (
        <div className={styles.uploadLoaderOverlay}>
          <p className={styles.uploadLoaderText}>{loaderText}</p>
          {(isUploading || isFetching) && (
            <div className={styles.uploadLoaderTrack}>
              <div className={styles.uploadLoaderBar} />
            </div>
          )}
        </div>
      )}

      {imageUrl && (
        <img
          src={imageUrl}
          loading="eager"
          alt=""
          className={`img-dragable ${styles.pointerEventsNone}`}
          draggable={false}
          style={{ display: imageLoaded ? 'block' : 'none' }}
          onLoad={() => setImageLoaded(true)}
        />
      )}

      {image && (
        <div className={`ghost-div-container ${styles.pointerEventsNone}`}>
          <div className={`w-layout-grid top-buttons-image-element ${styles.deleteButtonWrapperStyle}`}>
            <div
              onClick={handleDeleteClick}
              className={`delete-symbol ${styles.deleteButtonStyle}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDeleteClick(e); }}
              aria-label="Delete image"
            >
              <div className="icon small x2 w-embed">
                <DeleteIcon />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.fileName}>{fileName}</div>
    </div>
  );
};

const AlbumGrid = () => {
  const { id, lineItemId } = useParams();
  const queryClient = useQueryClient();
  const { setPhotosLoadingMessage, setApiError, uploadsPreview, photos } = useProductSelectionStore();
  const { Modal, setIsOpen } = useConfirmationModal();
  const pendingDeleteGuidRef = useRef(null);

  // State for the photo modal
  const [modalState, setModalState] = useState({
    isOpen: false,
    imageUrl: '',
    fileName: '',
  });

  const items = useMemo(() => initializeItems(photos || []), [photos]);

  const placeholders = (uploadsPreview || []).filter(
    p => !items.some(
      item => item?.attributes?.photoFileName === p.fileName && item?.attributes?.productType === 'albums'
    )
  );

  const handleCloseDeleteModal = useCallback(() => {
    pendingDeleteGuidRef.current = null;
  }, []);

  const openDeleteConfirmation = useCallback(
    (index) => {
      const guid = items[index]?.attributes?.guid;
      if (!guid) return;
      pendingDeleteGuidRef.current = guid;
      setIsOpen(true);
    },
    [items, setIsOpen],
  );

  const handleConfirmDelete = useCallback(() => {
    const guid = pendingDeleteGuidRef.current;
    setIsOpen(false);
    pendingDeleteGuidRef.current = null;
    if (!guid || !id) return;

    (async () => {
      try {
        setPhotosLoadingMessage('Deleting…');
        setApiError(null);

        await httpClient.delete(`lab_orders/${id}/albums/photographs/${guid}`);
        const queryKey = lineItemId
          ? ['labOrder', id, lineItemId]
          : ['photos', id];
        await queryClient.invalidateQueries({ queryKey });
      } catch (error) {
        const msg = apiErrorMessage(error);
        setApiError(msg);
        alert(msg);
      } finally {
        setPhotosLoadingMessage('');
      }
    })();
  }, [id, lineItemId, queryClient, setApiError, setIsOpen, setPhotosLoadingMessage]);

  // Handler to open the modal with the clicked image
  const handleImageClick = useCallback((imageUrl, fileName) => {
    setModalState({
      isOpen: true,
      imageUrl,
      fileName,
    });
  }, []);

  // Handler to close the modal
  const handleCloseModal = useCallback(() => {
    setModalState(prev => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const combinedItems = useMemo(() => {
    const list = items.map((item) => ({
      type: 'real',
      data: item,
      key: item.attributes?.photoFileName || getImageId(item, 0),
    }));

    const placeholderList = placeholders.map((p) => ({
      type: 'placeholder',
      data: p,
      key: p.fileName || p.id,
    }));

    return [...list, ...placeholderList];
  }, [items, placeholders]);

  return (
    <div className="div-block-327">
      <div id="left" className={`dragula-container ${styles.albumGridWrapper}`}>
        {combinedItems.map((entry, index) => (
          <ImageCard
            key={entry.key}
            image={entry.type === 'real' ? entry.data : null}
            placeholder={entry.type === 'placeholder' ? entry.data : null}
            index={index}
            onRequestDelete={openDeleteConfirmation}
            onImageClick={handleImageClick}
          />
        ))}
      </div>

      <div id="stroke" className="wrapper-dynamic-stroke">
        <div className="w-layout-grid dragula-container-copy">
          <div data-tippy-content="Grouped Pictures (2)" id="w-node-abb48915-8921-1799-14b7-2817332fdec4-a480b94b" className="stroke first-group" />
        </div>
      </div>

      <AlbumPhotoModal
        isOpen={modalState.isOpen}
        onClose={handleCloseModal}
        imageUrl={modalState.imageUrl}
        fileName={modalState.fileName}
      />
      <Modal handleCloseModal={handleCloseDeleteModal} onContinue={handleConfirmDelete} />
    </div>
  );
};

export default AlbumGrid;
